import { t, tp } from '@glory/i18n';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { toast, Toaster as SonnerToaster } from 'sonner';

import { TRANSITION } from '../lib/motion.tsx';

/**
 * Цвет плашки задаётся по виду тоста, а не в общем классе: sonner склеивает классы без слияния,
 * и общий `bg-strong` перебивал бы цвет ошибки (светлая плашка с белым текстом в тёмной теме).
 */
const plain = 'bg-strong text-on-strong';

const actionClass =
  'shrink-0 rounded-sm bg-accent px-2.5 py-1 text-small font-extrabold text-on-accent transition-[filter] hover:brightness-95 disabled:opacity-40 disabled:hover:brightness-100';

/** Место вывода уведомлений-тостов. Ставится один раз в корне приложения. */
export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-center"
      visibleToasts={3}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            'flex w-[min(92vw,420px)] items-center gap-3 rounded-lg px-3.5 py-2.5 shadow-float font-semibold',
          default: plain,
          success: plain,
          info: plain,
          warning: plain,
          loading: plain,
          error: 'bg-danger text-on-danger',
          content: 'min-w-0 flex-1',
          title: 'text-body',
          description: 'text-small opacity-75',
          icon: 'flex shrink-0 [&_svg]:size-4',
          actionButton: actionClass,
        },
      }}
    />
  );
}

/** Информационное сообщение. */
export function notify(message: string): void {
  toast(message);
}

export function notifyError(message: string): void {
  toast.error(message);
}

/** Сообщение с действием: «Анна не в чате · Добавить». */
export function notifyAction(options: { message: string; action: string; onAction: () => void }): void {
  toast(options.message, { duration: 8000, action: { label: options.action, onClick: options.onAction } });
}

/**
 * «Удалено · Отменить» вместо «Вы уверены?»: действие уже выполнено оптимистично,
 * кнопка возвращает всё как было.
 */
export function toastUndoable(options: { message?: string; onUndo: () => void; duration?: number }): void {
  toast(options.message ?? t('ui.deleted'), {
    duration: options.duration ?? 6000,
    action: { label: t('ui.undo'), onClick: options.onUndo },
  });
}

/**
 * «Удалено · Отменить» для необратимых действий (удалить ключ доступа, файл): элемент уже скрыт,
 * а сама операция выполняется, только когда тост закрылся без «Отменить».
 */
export function toastDeferred(options: {
  message: string;
  commit: () => void;
  onUndo: () => void;
  duration?: number;
}): void {
  let settled = false;
  const commit = () => {
    if (settled) return;
    settled = true;
    options.commit();
  };
  toast(options.message, {
    duration: options.duration ?? 6000,
    action: {
      label: t('ui.undo'),
      onClick: () => {
        if (settled) return;
        settled = true;
        options.onUndo();
      },
    },
    onAutoClose: commit,
    onDismiss: commit,
  });
}

/** Длина окружности кольца отсчёта (r = 8 в квадрате 20×20). */
const RING = 2 * Math.PI * 8;

const secondsLeft = (deadline: number) => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

/** Через сколько мс менять число: на границе секунды до срока, но не позже самого срока. */
const untilNextTick = (left: number) => Math.max(0, Math.min(left, (left % 1000 || 1000) + 10));

/**
 * Плашка с обратным отсчётом: фон заливается слева направо, справа кольцо с оставшимися секундами.
 * Всё считается от срока, а не от кадров: вернулись в фоновую вкладку — заливка, кольцо и число на месте.
 * Секунды диктору не читаются (скрыты), при появлении звучит сообщение и сколько есть на отмену.
 */
function CountdownToast({
  id,
  message,
  startedAt,
  deadline,
  onUndo,
}: {
  id: string | number;
  message: string;
  startedAt: number;
  deadline: number;
  onUndo: () => void;
}) {
  const [seconds, setSeconds] = useState(() => secondsLeft(deadline));
  const [span] = useState(() => ({
    seconds: Math.max(1, Math.ceil((deadline - startedAt) / 1000)),
    // Заливка и кольцо — CSS-анимации на всё окно, уже прошедшее время — отрицательной задержкой.
    timing: {
      animationDuration: `${String(deadline - startedAt)}ms`,
      animationDelay: `${String(startedAt - Date.now())}ms`,
    },
  }));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      clearTimeout(timer);
      const left = deadline - Date.now();
      setSeconds(Math.max(0, Math.ceil(left / 1000)));
      if (left <= 0) {
        toast.dismiss(id);
        return;
      }
      timer = setTimeout(tick, untilNextTick(left));
    };
    timer = setTimeout(tick, untilNextTick(deadline - Date.now()));
    // Таймеры фоновой вкладки браузер замедляет: при возвращении — сразу пересчёт от срока.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [deadline, id]);

  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit] motion-reduce:hidden"
      >
        <span
          className="block size-full origin-left bg-on-strong/15 motion-safe:animate-countdown-fill"
          style={span.timing}
        />
      </span>
      <div className="relative flex items-center gap-3">
        <span className="min-w-0 flex-1">
          {message}
          <span className="sr-only">{tp('ui.undoWithin', span.seconds)}</span>
        </span>
        <button
          type="button"
          className={actionClass}
          // После срока — тускнеет, пока плашка уходит; клик в щель между тиками только закрывает её.
          disabled={seconds === 0}
          onClick={() => {
            toast.dismiss(id);
            if (Date.now() < deadline) onUndo();
          }}
        >
          {t('ui.undo')}
        </button>
        <span aria-hidden="true" className="relative grid size-6 shrink-0 place-items-center">
          <svg viewBox="0 0 20 20" className="absolute inset-0 size-full -rotate-90">
            <circle cx="10" cy="10" r="8" fill="none" strokeWidth="2" className="stroke-current opacity-20" />
            {/* Без движения («Уменьшить движение») кольцо убывает ступенями — по секундам. */}
            <circle
              cx="10"
              cy="10"
              r="8"
              fill="none"
              strokeWidth="2"
              strokeDasharray={RING}
              className="stroke-accent motion-safe:animate-countdown-ring"
              style={{ strokeDashoffset: RING * (1 - seconds / span.seconds), ...span.timing }}
            />
          </svg>
          <AnimatePresence initial={false} mode="popLayout">
            <motion.span
              key={seconds}
              className="text-[0.625rem] leading-none font-extrabold tabular-nums"
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0, transition: TRANSITION.enter }}
              exit={{ opacity: 0, y: 3, transition: TRANSITION.exit }}
            >
              {seconds}
            </motion.span>
          </AnimatePresence>
        </span>
      </div>
    </>
  );
}

/**
 * «Отправлено · Отменить» с обратным отсчётом до настоящего срока, после которого отменить уже нельзя
 * (например, почтовый сервер держит письмо 10 секунд). Наведение курсора отсчёт не останавливает;
 * когда срок вышел, плашка закрывается сама.
 */
export function toastCountdown(options: {
  message: string;
  /** Срок в миллисекундах (`Date.now()`), до которого действует «Отменить». */
  deadline: number;
  /** Начало окна отмены: от него считается заливка. По умолчанию — сейчас. */
  startedAt?: number;
  onUndo: () => void;
}): void {
  const startedAt = options.startedAt ?? Date.now();
  toast.custom(
    (toastId) => (
      <CountdownToast
        id={toastId}
        message={options.message}
        startedAt={startedAt}
        deadline={options.deadline}
        onUndo={options.onUndo}
      />
    ),
    // Свой таймер у sonner встаёт на паузу при наведении — по сроку плашку закрывает сам отсчёт.
    { duration: Infinity },
  );
}
